import { useCallback, useEffect, useRef, useState } from 'react';

import { createPortal } from 'react-dom';

import { AtIcon, Search01Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import { chipHTML, htmlToPlainText, textToHTML } from 'helpers/param-chip-markup';
import PropTypes from 'prop-types';

/** Insert HTML at the current cursor position */
function insertAtCursor(html) {
  const sel = window.getSelection();
  if (!sel || !sel.rangeCount) return;
  const range = sel.getRangeAt(0);
  range.deleteContents();
  const temp = document.createElement('div');
  temp.innerHTML = html;
  const frag = document.createDocumentFragment();
  let lastNode;
  while (temp.firstChild) {
    lastNode = frag.appendChild(temp.firstChild);
  }
  range.insertNode(frag);
  if (lastNode) {
    range.setStartAfter(lastNode);
    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);
  }
}

export const ParamTextarea = ({
  value = '',
  onChange,
  textParams = {},
  placeholder = 'Enter question...',
  error = false,
  className,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });
  const wrapperRef = useRef(null);
  const atButtonRef = useRef(null);
  const dropdownRef = useRef(null);
  const editableRef = useRef(null);
  const lastPlainRef = useRef(null);

  const updateDropdownPosition = useCallback(() => {
    const el = atButtonRef.current || wrapperRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setDropdownPosition({
      top: rect.bottom + 4,
      right: window.innerWidth - rect.right,
    });
  }, []);

  // Set initial HTML on mount
  useEffect(() => {
    if (!editableRef.current) return;
    editableRef.current.innerHTML = textToHTML(value);
    lastPlainRef.current = value;
  }, []);

  // Sync when value changes externally (e.g. form reset)
  useEffect(() => {
    if (!editableRef.current) return;
    if (lastPlainRef.current === value) return;
    editableRef.current.innerHTML = textToHTML(value);
    lastPlainRef.current = value;
  }, [value]);

  const handleInput = useCallback(() => {
    if (!editableRef.current) return;
    const text = htmlToPlainText(editableRef.current);
    lastPlainRef.current = text;
    onChange(text);
  }, [onChange]);

  const handleKeyDown = useCallback(
    e => {
      if (e.key === '@') {
        e.preventDefault();
        setSearch('');
        setMenuOpen(true);
        updateDropdownPosition();
      }
    },
    [updateDropdownPosition],
  );

  const handleInsertParam = useCallback(
    key => {
      if (!editableRef.current) return;
      editableRef.current.focus();
      insertAtCursor(chipHTML(key));
      const text = htmlToPlainText(editableRef.current);
      lastPlainRef.current = text;
      onChange(text);
      setMenuOpen(false);
      setSearch('');
    },
    [onChange],
  );

  const handleChipRemoveClick = useCallback(
    e => {
      const removeBtn = e.target.closest('.param-chip-remove');
      if (!removeBtn || !editableRef.current) return;
      const chip = removeBtn.closest('.param-chip');
      if (!chip) return;
      e.preventDefault();
      e.stopPropagation();
      chip.remove();
      const text = htmlToPlainText(editableRef.current);
      lastPlainRef.current = text;
      onChange(text);
    },
    [onChange],
  );

  // Close when clicking outside wrapper or portaled dropdown
  useEffect(() => {
    const handleClickOutside = e => {
      if (wrapperRef.current?.contains(e.target)) return;
      if (dropdownRef.current?.contains(e.target)) return;
      setMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // When menu opens, position dropdown and keep it aligned on scroll/resize (portal escapes overflow)
  useEffect(() => {
    if (!menuOpen) return;
    updateDropdownPosition();
    window.addEventListener('scroll', updateDropdownPosition, true);
    window.addEventListener('resize', updateDropdownPosition);
    return () => {
      window.removeEventListener('scroll', updateDropdownPosition, true);
      window.removeEventListener('resize', updateDropdownPosition);
    };
  }, [menuOpen, updateDropdownPosition]);

  // Close dropdown on Escape (works when focus is in contenteditable or in dropdown search)
  useEffect(() => {
    if (!menuOpen) return;
    const handleEscape = e => {
      if (e.key === 'Escape') {
        setMenuOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [menuOpen]);

  const paramKeys = Object.keys(textParams);
  const filteredKeys = search
    ? paramKeys.filter(k => k.toLowerCase().includes(search.toLowerCase()))
    : paramKeys;

  const dropdownContent = menuOpen && (
    <div
      ref={dropdownRef}
      data-click-outside-ignore
      className="fixed bg-white border border-neutral-4 rounded-2xl p-2 flex flex-col gap-1.5 shadow-xl w-[min(40rem,calc(100vw-2rem))] z-[9999]"
      style={{
        top: dropdownPosition.top,
        right: dropdownPosition.right,
      }}>
      {/* Search */}
      <div className="flex items-center gap-1.5 bg-neutral-2 border border-neutral-4 rounded-full px-2 py-2">
        <HugeiconsIcon icon={Search01Icon} size="16" strokeWidth="2" className="text-neutral-6" />
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search for a variable..."
          className="bg-transparent outline-none text-xl text-neutral-6 placeholder:text-neutral-6 w-full"
        />
      </div>

      {/* Param chips */}
      {filteredKeys.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {filteredKeys.map(key => (
            <button
              key={key}
              type="button"
              onClick={() => handleInsertParam(key)}
              className="flex items-center gap-1 px-2 py-1 bg-primary-4/15 border border-primary-5 rounded-2xl text-xl font-regular text-primary-5 hover:bg-primary-1 hover:border-primary-3 hover:text-primary-5 transition-colors whitespace-nowrap w-max">
              <HugeiconsIcon icon={AtIcon} size="16" strokeWidth="2" className="text-primary-5" />
              {key}
            </button>
          ))}
        </div>
      ) : (
        <p className="text-xs text-neutral-5 text-center py-1">No variables found</p>
      )}
    </div>
  );

  return (
    <div ref={wrapperRef} className={cn('relative', className)}>
      {/* Input row */}
      <div
        className={cn(
          'flex items-start gap-1.5 bg-neutral-2 border border-neutral-4 rounded-2xl px-3 py-2 min-h-[36px]',
          error && 'border-red-2',
        )}>
        {/* Contenteditable area */}
        <div
          ref={editableRef}
          contentEditable
          suppressContentEditableWarning
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          onClick={handleChipRemoveClick}
          data-placeholder={placeholder}
          className="flex-1 min-w-0 outline-none text-xl text-neutral-7 mt-1 break-words empty:before:content-[attr(data-placeholder)] empty:before:text-neutral-5 empty:before:pointer-events-none cursor-text"
        />

        <div
          ref={atButtonRef}
          className="flex items-center justify-center border border-primary-5 rounded-xl p-1 shrink-0 bg-neutral-1 shadow-md cursor-pointer">
          <HugeiconsIcon
            icon={AtIcon}
            size="16"
            strokeWidth="2"
            className="text-primary-5"
            onClick={() => {
              setMenuOpen(v => {
                if (!v) updateDropdownPosition();
                return !v;
              });
            }}
          />
        </div>
      </div>

      <div className="flex mt-2">
        <span className="text-xl text-text-tertiary text-center py-1">
          Press{' '}
          <span className="bg-neutral-2 rounded-xl px-1 py-0.5 text-neutral-6 border border-neutral-4">
            @
          </span>{' '}
          to insert a variable
        </span>
      </div>

      {/* Param insertion dropdown — portaled so it isn't clipped by overflow in QuestionRow/QuestionsTab */}
      {createPortal(dropdownContent, document.body)}
    </div>
  );
};

ParamTextarea.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  textParams: PropTypes.object,
  placeholder: PropTypes.string,
  error: PropTypes.bool,
  className: PropTypes.string,
};

export default ParamTextarea;
